import type { ItemQuoteInput, TransactionIdentifier } from "@opensea/graphql"
import { graphql, useQuery } from "@opensea/graphql"
import { omit } from "es-toolkit/object"
import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { Button } from "../../../../../components/Button"
import { TransactionFullScreen } from "../../../../../components/TransactionFullScreen"
import { TransactionReceipt } from "../../../../../components/TransactionReceipt"
import { TransactionReceiptFragment } from "../../../../../components/TransactionReceipt/TransactionReceipt.fragment"
import { FlowState } from "../../../../../stores/flowStore"
import {
  useSetReceiptStatus,
  useSetReceiptStatusFragment,
} from "../../../../hooks/useSetReceiptStatus"

const FETCH_INTERVAL = 500

const query = graphql(
  `
    query AcceptOfferFlowStatusQuery(
      $transactionIdentifiers: [TransactionIdentifier!]!
      $itemQuotes: [ItemQuoteInput!]!
      $swapProvider: SwapProviderType!
    ) {
      buyReceipt(
        transactionIdentifiers: $transactionIdentifiers
        itemQuotes: $itemQuotes
        swapProvider: $swapProvider
      ) {
        status
        itemReceipts {
          __typename
          item {
            id
          }
        }
        failedItemReceipts {
          __typename
          item {
            id
          }
        }
        ...useSetReceiptStatus
        ...TransactionReceipt
      }
    }
  `,
  [TransactionReceiptFragment, useSetReceiptStatusFragment],
)

type Props = {
  transactionIdentifiers: TransactionIdentifier[]
  itemQuotes: ItemQuoteInput[]
  itemImageMap: Record<string, string | null>
}

export const AcceptOfferStatus = ({
  transactionIdentifiers,
  itemQuotes,
  itemImageMap,
}: Props) => {
  const [result, reexecuteQuery] = useQuery({
    query,
    variables: {
      transactionIdentifiers,
      itemQuotes,
      swapProvider: "LOCAL_BUY_NFT",
    },
    pause: true,
  })

  const t = useTranslations("wallet.AcceptOfferStatus")

  useEffect(
    function pollForReceipt() {
      if (
        result.fetching ||
        (result.data?.buyReceipt &&
          result.data.buyReceipt.status !== "PENDING") ||
        transactionIdentifiers.length === 0
      ) {
        return
      }

      const timerId = setTimeout(() => {
        reexecuteQuery({ requestPolicy: "network-only" })
      }, FETCH_INTERVAL)

      return () => clearTimeout(timerId)
    },
    [
      result.fetching,
      reexecuteQuery,
      result.data?.buyReceipt,
      transactionIdentifiers.length,
    ],
  )

  const receipt = result.data?.buyReceipt

  useSetReceiptStatus(receipt)

  const [receiptOpen, setReceiptOpen] = useState(false)

  if (!receipt || receipt.status === "PENDING") {
    const pendingImages = Object.values(itemImageMap)
    return (
      <TransactionFullScreen
        body={t("pending.description")}
        images={pendingImages}
        loading
        title={t("pending.title")}
      />
    )
  }

  if (receipt.status === "FAILED") {
    return (
      <TransactionFullScreen
        body={t("failed.description")}
        bottom={
          receiptOpen ? (
            <TransactionReceipt flowType="acceptOffer" receipt={receipt} />
          ) : (
            <Button onClick={() => setReceiptOpen(true)} variant="secondary">
              {t("viewReceipt")}
            </Button>
          )
        }
        images={receipt.failedItemReceipts.map(item =>
          item.item ? itemImageMap[item.item.id] : null,
        )}
        monochrome
        title={t("failed.title")}
      />
    )
  }

  if (receipt.status === "PARTIAL_SUCCESS") {
    return (
      <TransactionFullScreen
        body={t("partialSuccess.description", {
          itemsSoldCount: receipt.itemReceipts.length,
          itemsFailedCount: receipt.failedItemReceipts.length,
        })}
        bottom={
          receiptOpen ? (
            <TransactionReceipt flowType="acceptOffer" receipt={receipt} />
          ) : (
            <Button onClick={() => setReceiptOpen(true)} variant="secondary">
              {t("viewReceipt")}
            </Button>
          )
        }
        images={receipt.itemReceipts.map(item =>
          item.item ? itemImageMap[item.item.id] : null,
        )}
        title={t("partialSuccess.title")}
      />
    )
  }

  return (
    <TransactionFullScreen
      bottom={
        receiptOpen ? (
          <TransactionReceipt flowType="acceptOffer" receipt={receipt} />
        ) : (
          <Button onClick={() => setReceiptOpen(true)} variant="secondary">
            {t("viewReceipt")}
          </Button>
        )
      }
      confetti
      images={receipt.itemReceipts.map(item =>
        item.item ? itemImageMap[item.item.id] : null,
      )}
      title={t("success.title", {
        count: receipt.itemReceipts.length,
      })}
    />
  )
}

export function AcceptOfferFlowStatus() {
  const { receiptTransactions, itemQuotes, itemImageMap } =
    useSnapshot(FlowState)

  const itemQuoteInputs = useMemo(
    () => itemQuotes.map(itemQuote => omit(itemQuote, ["orderId"])),
    [itemQuotes],
  )

  return (
    <AcceptOfferStatus
      itemImageMap={itemImageMap}
      itemQuotes={itemQuoteInputs}
      transactionIdentifiers={receiptTransactions.map(txn => ({
        chain: txn.chain,
        transactionHash: txn.hash,
      }))}
    />
  )
}
